import { NavLink } from 'react-router-dom';
import { useHighestRole } from '../auth/useAuth';
import type { RoleToken } from '../auth/types';
import {
  IconBarChart,
  IconBot,
  IconBell,
  IconClipboardList,
  IconFileText,
  IconHome,
  IconLayers,
  IconLifeBuoy,
  IconShieldCheck,
  type IconProps,
} from '../design-system/icons';
import './SideNav.css';

/** Vite `define` constant (vite.config.ts) — the build's version
 *  string, threaded from the APP_VERSION build arg. Declared here the
 *  same way `security-headers.ts` / `telemetry.ts` declare it. */
declare const __APP_VERSION__: string;

interface NavEntry {
  to: string;
  label: string;
  allowedRoles: RoleToken[] | 'all';
  Icon: (props: IconProps) => React.ReactElement;
}

// Profile + Preferences are no longer in the side nav — Profile now lives
// behind the top-right avatar menu (Gmail-style). The full set of surfaces
// an operator can navigate to from the rail is:
//   Overview · Tasks · Alerts · Fleet · Devices · Metrics · Support · Audit · Access
// (task-first ordering: what to act on, then the fleet/device inventory
// behind it, then reporting/admin surfaces last — Access last since it's
// platform_admin-only and read-only in Phase 1.)
// A standalone Map tab used to exist here — removed (plan §Phase 4E:
// "serves no real purpose") since Overview already has the same live
// map embedded, doors/lifts/robots clickable, and nothing on the
// dedicated screen wasn't already there.
const NAV_ENTRIES: NavEntry[] = [
  { to: '/overview', label: 'Overview', allowedRoles: 'all', Icon: IconHome },
  { to: '/tasks', label: 'Tasks', allowedRoles: 'all', Icon: IconClipboardList },
  { to: '/alerts', label: 'Alerts', allowedRoles: ['nexsoss:platform_admin', 'nexsoss:site_supervisor', 'nexsoss:service_operator'], Icon: IconBell },
  { to: '/fleet', label: 'Robots', allowedRoles: 'all', Icon: IconBot },
  { to: '/devices', label: 'Devices', allowedRoles: ['nexsoss:platform_admin', 'nexsoss:site_supervisor', 'nexsoss:service_operator'], Icon: IconLayers },
  { to: '/metrics', label: 'Metrics', allowedRoles: ['nexsoss:platform_admin', 'nexsoss:site_supervisor', 'nexsoss:service_operator'], Icon: IconBarChart },
  { to: '/support', label: 'Support', allowedRoles: ['nexsoss:platform_admin', 'nexsoss:site_supervisor', 'nexsoss:service_operator'], Icon: IconLifeBuoy },
  { to: '/audit', label: 'Audit', allowedRoles: ['nexsoss:platform_admin', 'nexsoss:site_supervisor'], Icon: IconFileText },
  { to: '/access', label: 'Access', allowedRoles: ['nexsoss:platform_admin'], Icon: IconShieldCheck },
];

export function SideNav({
  rail = false,
  onToggleRail,
}: {
  /**
   * Icons only (`state/nav-rail.ts`): the person's choice, drawn from
   * 1024px up (`SideNav.css`). Below that the nav is already a rail or a
   * drawer and this changes nothing but the hover labels.
   */
  rail?: boolean;
  /** Present: draws the "Shrink menu to icons" / "Show menu labels"
   *  button at the foot of the menu (desktop tier only). */
  onToggleRail?: () => void;
} = {}) {
  const role = useHighestRole();
  const entries = NAV_ENTRIES.filter((e) => e.allowedRoles === 'all' || (role && e.allowedRoles.includes(role)));
  const railLabel = rail ? 'Show menu labels' : 'Shrink menu to icons';

  return (
    <nav className="nx-side-nav" aria-label="Primary" data-rail={rail ? 'true' : 'false'}>
      <div className="nx-side-nav__brand">
        {/* The wordmark is the product's one link out to nexsoss.ai.
            The "RMF Web" sub-line is gone — it named an upstream
            codebase, not the product an operator is using. */}
        <a
          className="nx-side-nav__brand-name"
          href="https://nexsoss.ai"
          target="_blank"
          rel="noreferrer"
          aria-label="NEXSOSS — nexsoss.ai"
        >
          <span className="nx-side-nav__brand-full">NEXSOSS</span>
          {/* The rail's compact mark; the link's name is its label. */}
          <span className="nx-side-nav__brand-mark" aria-hidden="true">
            N
          </span>
        </a>
      </div>
      <ul className="nx-side-nav__list">
        {entries.map((entry) => (
          <li key={entry.to}>
            <NavLink
              to={entry.to}
              /* THE RAIL HAS NO OTHER NAME. Between 768 and 1023px the
                 label span is `display: none` -- which removes it from
                 the accessibility tree, not just from view -- and the
                 icon is `aria-hidden`. Without this the tablet nav was
                 nine unlabelled buttons to a screen reader, and nine
                 unexplained glyphs to anyone hovering them. `title`
                 carries the sighted half, `aria-label` the other; both
                 are harmless at desktop width where the text is
                 visible anyway. */
              // In the chosen rail the label is drawn as a tooltip on
              // hover AND keyboard focus (`SideNav.css`), which `title`
              // cannot do; two tooltips for one link would be noise.
              title={rail ? undefined : entry.label}
              aria-label={entry.label}
              className={({ isActive }) =>
                `nx-side-nav__link${isActive ? ' nx-side-nav__link--active' : ''}`
              }
            >
              <entry.Icon size={17} className="nx-side-nav__icon" />
              <span>{entry.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
      {onToggleRail && (
        <button
          type="button"
          className="nx-side-nav__rail-toggle"
          onClick={onToggleRail}
          aria-label={railLabel}
          title={railLabel}
          data-testid="side-nav-rail-toggle"
        >
          <span aria-hidden="true">{rail ? '»' : '« Icons only'}</span>
        </button>
      )}
      {/* Which build am I looking at — the first thing support asks
          and, until now, a question the UI could not answer anywhere.
          `__APP_VERSION__` is Vite `define`-substituted from the
          APP_VERSION build arg (see Dockerfile / build-images.sh);
          dev builds show `dev-<date>`. */}
      <div className="nx-side-nav__version" data-testid="app-version">
        v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'}
      </div>
    </nav>
  );
}